import React from 'react';
import styled, { css } from 'styled-components';
import { Entity, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { GridLoader } from '@shapeable/ui';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { AnimatePresence, LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { EntityPublicationTimelineItem } from './entity-publication-timeline-item';

const cls = classNames('publication-animated-grid');

const DEFAULT_ITEMS_PER_PAGE = 15;

const ENTER_SPRING = {
  type: 'spring',
  stiffness: 420,
  damping: 34,
  mass: 0.75,
};

const EXIT_TRANSITION = {
  duration: 0.18,
  ease: [0.4, 0, 1, 1],
};

const LAYOUT_SPRING = {
  type: 'spring',
  stiffness: 520,
  damping: 40,
  mass: 0.55,
};

// -------- Props -------->

export type PublicationAnimatedGridProps = {
  items: Entity[];
  className?: string;
  itemsPerPage?: number;
};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    position: relative;
    width: 100%;
    border-radius: ${theme.UNIT(1)};
  `,
});

const GridStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: visible;
  `,
});

const ItemShellStyles = breakpoints({
  base: css`
    width: 100%;
    overflow: visible;
    transform-origin: center top;
  `,
});

const Container = styled.div`${ContainerStyles}`;
const Grid = styled.div`${GridStyles}`;
const ItemShell = styled(motion.div).attrs(cls.attr('item'))`${ItemShellStyles}`;

const Loader = styled(GridLoader)`
  width: 100%;
`;

export const PublicationAnimatedGrid: Shapeable.FC<PublicationAnimatedGridProps> = (props) => {
  const { items, className, itemsPerPage = DEFAULT_ITEMS_PER_PAGE } = props;
  const prefersReducedMotion = useReducedMotion();

  const itemSignature = items.map((item) => item.id).join(',');
  const lastSignatureRef = React.useRef(itemSignature);
  const previousVisibleCount = React.useRef(0);
  const contentMotionEnabled = React.useRef(false);

  const [displayCount, setDisplayCount] = React.useState(itemsPerPage);
  const [visibleItems, setVisibleItems] = React.useState<Entity[]>([]);

  const hasNextPage = displayCount < items.length;

  const onLoadMore = React.useCallback(() => {
    setDisplayCount((count) => Math.min(count + itemsPerPage, items.length));
  }, [items.length, itemsPerPage]);

  const [sentryRef] = useInfiniteScroll({
    loading: false,
    hasNextPage,
    onLoadMore,
    rootMargin: '0px 0px 600px 0px',
  });

  React.useEffect(() => {
    if (visibleItems.length > 0 || items.length === 0) {
      return;
    }

    const initial = items.slice(0, displayCount);
    setVisibleItems(initial);
    previousVisibleCount.current = initial.length;
  }, [items, displayCount, visibleItems.length]);

  React.useLayoutEffect(() => {
    if (lastSignatureRef.current === itemSignature) {
      return;
    }

    lastSignatureRef.current = itemSignature;
    contentMotionEnabled.current = true;
    setDisplayCount(itemsPerPage);

    const nextItems = items.slice(0, itemsPerPage);
    setVisibleItems(nextItems);
    previousVisibleCount.current = nextItems.length;
  }, [itemSignature, items, itemsPerPage]);

  React.useEffect(() => {
    if (lastSignatureRef.current !== itemSignature) {
      return;
    }

    const nextItems = items.slice(0, displayCount);

    if (nextItems.length <= visibleItems.length) {
      return;
    }

    setVisibleItems(nextItems);
  }, [displayCount, items, itemSignature, visibleItems.length]);

  const isLoadMoreEnter =
    lastSignatureRef.current === itemSignature
    && visibleItems.length > previousVisibleCount.current;

  React.useEffect(() => {
    if (visibleItems.length >= previousVisibleCount.current) {
      previousVisibleCount.current = visibleItems.length;
    }
  }, [visibleItems.length]);

  const getEnterDelay = (index: number) => {
    if (prefersReducedMotion) {
      return 0;
    }

    const staggerBase = isLoadMoreEnter ? previousVisibleCount.current : 0;

    return Math.min(Math.max(0, index - staggerBase) * 0.04, 0.14);
  };

  const getShellTransition = (index: number) => {
    if (prefersReducedMotion) {
      return { duration: 0 };
    }

    return {
      layout: LAYOUT_SPRING,
      default: {
        ...ENTER_SPRING,
        delay: getEnterDelay(index),
      },
      exit: EXIT_TRANSITION,
    };
  };

  const shouldAnimateContent = (index: number) => {
    if (prefersReducedMotion) {
      return false;
    }

    if (contentMotionEnabled.current) {
      return true;
    }

    // Infinite-scroll rows keep enter animation props on every render. Using
    // isLoadMoreEnter alone toggled enterDelay off after one frame and left
    // cards stuck at opacity: 0 while date chrome still appeared.
    return index >= itemsPerPage;
  };

  const getContentEnterDelay = (index: number) => {
    return shouldAnimateContent(index) ? getEnterDelay(index) : undefined;
  };

  return (
    <Container className={cls.name(className)}>
      <LayoutGroup id="publication-grid">
        <Grid>
          <AnimatePresence initial={false}>
            {visibleItems.map((item, index) => (
              <ItemShell
                key={item.id}
                layout="position"
                initial={prefersReducedMotion ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -10 }}
                transition={getShellTransition(index)}
              >
                <EntityPublicationTimelineItem
                  entity={item}
                  isLast={index === visibleItems.length - 1 && !hasNextPage}
                  enterDelay={getContentEnterDelay(index)}
                />
              </ItemShell>
            ))}
          </AnimatePresence>
        </Grid>
      </LayoutGroup>
      {hasNextPage && <Loader ref={sentryRef} />}
    </Container>
  );
};

PublicationAnimatedGrid.cls = cls;
